import { useEffect, useRef, useState } from 'react';

import { cubicBezier } from 'framer-motion';
import { observer } from 'mobx-react';

import gcCrownIcon from '../../../assets/missions/gcCrown.png';
import scCrownIcon from '../../../assets/missions/scCrown.png';
import type { CoinType } from '../../../stores/cointype/CoinTypeStore';
import { MissionsWithIntegerProgress, type MissionResponse } from '../../../stores/missions/types';
import { useStores } from '../../../stores/useStores';
import { GAME_CARD_ID_PREFIX } from '../../GamesList/constants';

import { GiftTooltip } from './GiftTooltip';
import classes from './MissionsLobbyProgressBar.module.scss';

// Progress text for a mission at a given fill ratio (0–1), counting up with the bar.
// Round (not floor) the counted value so small goals don't appear stuck one short near the end.
const missionProgressText = (mission: MissionResponse, ratio: number): string => {
  const integer = MissionsWithIntegerProgress.includes(mission.missionType);
  const displayValue = Math.round(ratio * mission.goal);
  const displayGoal = integer ? Math.floor(mission.goal) : mission.goal;
  return `${displayValue}/${displayGoal}`;
};

// Bar fill timing: fill up, then hold at full (gift animation plays) before snapping to 0 for the next.
const FILL_SEC = 0.8;
const HOLD_MS = 650;

// Easing for the bar fill (framer-motion's cubic-bezier easing helper).
const FILL_EASE = cubicBezier(0.32, 0, 0.67, 0);

const Timer = observer(({ coinType }: { coinType: CoinType }) => {
  const { missionsStore } = useStores();
  const time = missionsStore.getTimer(coinType);
  if (!time) return null;
  return (
    <div className={classes.timer}>
      <svg fill="none" height="32" viewBox="0 -4 118 36" width="118" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="topHalf">
            <rect height="18" width="126" x="-4" y="-4" />
          </clipPath>
        </defs>

        <path
          clipPath="url(#topHalf)"
          d="M0 13.6919L12.7432 3.76203C15.8587 1.3343 19.8261 0 23.9278 0H93.2143C97.316 0 101.283 1.3343 104.399 3.76203L117.142 13.6919L104.399 23.6218C101.283 26.0496 97.316 27.3839 93.2143 27.3839H23.9278C19.8261 27.3839 15.8587 26.0496 12.7432 23.6218L0 13.6919Z"
          fill="none"
          stroke="#732D95"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="5"
        />

        <path
          d="M0 13.6919L12.7432 3.76203C15.8587 1.3343 19.8261 0 23.9278 0H93.2143C97.316 0 101.283 1.3343 104.399 3.76203L117.142 13.6919L104.399 23.6218C101.283 26.0496 97.316 27.3839 93.2143 27.3839H23.9278C19.8261 27.3839 15.8587 26.0496 12.7432 23.6218L0 13.6919Z"
          fill="#590781"
        />
        <text fill="white" fontSize="18" textAnchor="middle" x="50%" y="50%">
          {time}
        </text>
      </svg>
    </div>
  );
});

export const MissionsLobbyProgressBar = observer(() => {
  const { missionsStore, coinTypeStore } = useStores();
  const { coinType } = coinTypeStore;
  const { currentMissions, activeMission, hasOldRewards, unclaimedMissions, isActiveMissionGrandPrize } = missionsStore;
  const [playAnimation, setPlayAnimation] = useState(false);

  // Completed missions to celebrate, one fill-to-end each (old rewards first when present).
  const completedSource = hasOldRewards
    ? unclaimedMissions.filter((m) => m.status === 'completed')
    : (currentMissions?.missions ?? []).filter((m) => m.status === 'completed');
  const completedCount = completedSource.length;
  // Signature of the current completed set; the celebration replays only when this changes.
  const celebrateKey = `${hasOldRewards ? 'old' : 'cur'}:${completedSource.map((m) => m.id).join(',')}`;

  // The mission whose progress the bar represents. With old rewards the current set is hidden, so
  // take it from the unclaimed missions; otherwise the active mission (falling back to a completed
  // one when there's no active mission) so the text is never blank/zero.
  const primaryMission = hasOldRewards
    ? (completedSource[0] ?? unclaimedMissions[0] ?? null)
    : (activeMission ?? completedSource[0] ?? null);
  const { progress = 0, goal = 0, missionType, packageData, game } = primaryMission ?? {};
  const isInteger = missionType ? MissionsWithIntegerProgress.includes(missionType) : false;
  const displayProgress = isInteger ? Math.floor(progress) : progress;
  const displayGoal = isInteger ? Math.floor(goal) : goal;
  const activePercentage = goal > 0 ? Math.min((progress / goal) * 100, 100) : 0;

  // Unclaimed (previous-set) rewards take priority and keep the bar full to flag "claim me".
  // With no active mission (everything done/claimable) the bar also stays full; otherwise it
  // settles on the active mission's progress.
  const restingWidth = hasOldRewards || !activeMission ? 100 : activePercentage;

  const fillRef = useRef<HTMLDivElement>(null);
  const animationEndTimeout = useRef<NodeJS.Timeout | number>(0);
  // Animated progress text shown during the celebration (null = use the static display below).
  const [barText, setBarText] = useState<string | null>(null);

  useEffect(() => {
    const el = fillRef.current;
    if (!el) return;

    let cancelled = false;
    let rafId = 0;
    let holdTimer: ReturnType<typeof setTimeout> | undefined;

    const setWidth = (v: number) => {
      el.style.width = `${v}%`;
    };

    // Tween the bar width from→to while counting the text up/down for `mission`. Resolves on finish.
    const animateBar = (mission: MissionResponse, from: number, to: number, durSec: number) =>
      new Promise<void>((resolve) => {
        const startT = performance.now();
        const durMs = durSec * 1000;
        const step = (now: number) => {
          if (cancelled) return resolve();
          const p = Math.min((now - startT) / durMs, 1);
          const eased = FILL_EASE(p);
          const w = from + (to - from) * eased;
          setWidth(w);
          setBarText(missionProgressText(mission, w / 100));
          if (p < 1) {
            rafId = requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        rafId = requestAnimationFrame(step);
      });

    // Width-only tween (no text), used to settle when there's nothing to celebrate.
    const tweenWidth = (from: number, to: number, durSec: number) =>
      new Promise<void>((resolve) => {
        const startT = performance.now();
        const durMs = durSec * 1000;
        const step = (now: number) => {
          if (cancelled) return resolve();
          const p = Math.min((now - startT) / durMs, 1);
          const eased = FILL_EASE(p);
          setWidth(from + (to - from) * eased);
          if (p < 1) {
            rafId = requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        rafId = requestAnimationFrame(step);
      });
    const hold = (ms: number) => new Promise<void>((resolve) => (holdTimer = setTimeout(resolve, ms)));

    const run = async () => {
      setPlayAnimation(false);
      // Celebrate only when there are completed missions and this exact set hasn't been celebrated yet.
      const shouldCelebrate = completedCount > 0 && celebrateKey !== missionsStore.lastCelebratedKey;

      if (!shouldCelebrate) {
        setBarText(null);
        await tweenWidth(parseFloat(el.style.width) || 0, restingWidth, FILL_SEC);
        return;
      }

      const completed = missionsStore.completedToCelebrate;

      // One fill-to-100 per completed mission; between cycles the bar snaps back to 0 (no drain).
      // Each fill counts its own mission's progress text (so the goal changes per mission).
      // When the bar reaches the end, play the gift animation, hold full briefly, then stop.
      // Chained via reduce (instead of a for-await loop) so each cycle runs strictly after the previous.
      await completed.reduce(
        (prev: Promise<void>, mission) =>
          prev.then(async () => {
            if (cancelled) return;
            setPlayAnimation(false);
            setWidth(0);
            await animateBar(mission, 0, 100, FILL_SEC);
            if (cancelled) return;
            setPlayAnimation(true); // bar reached the end → play the gift animation
            await hold(HOLD_MS);
            if (cancelled) return;
            setPlayAnimation(false); // stop the animation after holding full
          }),
        Promise.resolve(),
      );

      // Settle on the active mission's real progress: snap to 0, then fill the active mission to its
      // real progress (skip when staying full, e.g. old rewards / nothing active).
      const active = missionsStore.activeMission;
      if (!cancelled && restingWidth < 100 && active && completed.length > 0) {
        setWidth(0);
        await animateBar(active, 0, restingWidth, FILL_SEC);
      }

      if (cancelled) return;
      missionsStore.setLastCelebratedKey(celebrateKey);
      // Keep the last animated text as the resting value (settling to the static display here would
      // glitch back to the first mission); the non-celebrate path clears it on the next data change.
      // Once the whole fill animation finishes, open the missions dialog.
      missionsStore.openDialog();
    };

    void run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      clearTimeout(holdTimer);
    };
  }, [celebrateKey, completedCount, restingWidth, missionsStore]);

  useEffect(() => {
    return () => clearTimeout(animationEndTimeout.current);
  }, []);

  if (!currentMissions || !primaryMission) return null;

  const handleClick = () => {
    missionsStore.openDialog();
  };

  const onGameClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    // Use the displayed mission's game (primaryMission), which may not be the active one.
    const gameId = game?.gameId;
    if (!gameId) return;
    const gameCard = document.querySelector(`#${GAME_CARD_ID_PREFIX}${gameId}`);

    if (gameCard) {
      gameCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      gameCard.classList.add('game-card-highlight');
      clearTimeout(animationEndTimeout.current);
      animationEndTimeout.current = setTimeout(() => {
        gameCard.classList.remove('game-card-highlight');
        clearTimeout(animationEndTimeout.current);
        animationEndTimeout.current = 0;
      }, 4000);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.crownIconWrapper}>
        {game?.gameId ? (
          <div className={classes.gameIconWrapper} onClick={onGameClick}>
            <img alt="game" className={classes.gameIcon} src={game.gameImageUrl} />
          </div>
        ) : (
          <img alt="crown" className={classes.crownIcon} src={coinType === 'GC' ? gcCrownIcon : scCrownIcon} />
        )}
      </div>

      <Timer coinType={coinType} />
      <div className={classes.barWrapper} onClick={handleClick}>
        <div className={classes.bar}>
          <div ref={fillRef} className={classes.fill} />
          <span className={classes.progressText}>{barText ?? `${displayProgress}/${displayGoal}`}</span>
        </div>
      </div>

      <div className={classes.giftTooltipWrapper}>
        <GiftTooltip
          bundleItems={packageData?.bundleItems || []}
          gcCoin={packageData?.gcCoin || 0}
          playAnimation={playAnimation || isActiveMissionGrandPrize}
          scCoin={packageData?.scCoin || 0}
        />
      </div>
    </div>
  );
});
